import { StreamTextUtils } from "./StreamTextUtils";

/** Maps UI / input codes to API: en | ma | hi */
function normalizeDocsChatbotLanguage(code) {
  const c = String(code || "")
    .trim()
    .toLowerCase();
  if (c === "en") return "en";
  if (c === "hi") return "hi";
  if (c === "ma" || c === "mr") return "ma";
  if (c === "gj" || c === "gu") return "gj";
  return "ma";
}

export class DocsChatbotStreamClient {
  constructor({ baseURL, streamTextUtils } = {}) {
    this.baseURL = baseURL;
    this.streamTextUtils = streamTextUtils || new StreamTextUtils();
  }

  async stream({
    message,
    token,
    selectedDepartments,
    messages = [],
    useMemory = false,
    language = "ma",
    conversationId,
    onDelta,
    onDone,
    onError,
  }) {
    const endpoint =
      this.baseURL +
      `/chatbot/query_docs_chatbot/${conversationId ? `?conversation_id=${encodeURIComponent(conversationId)}` : ""
      }`;
    const requestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      mode: "cors",
      cache: "no-store",
      body: JSON.stringify({
        query: message,
        docs_ids: [""],
        from_date: "",
        to_date: "",
        code_number: "",
        gad_type: (() => {
          const normalized = Array.isArray(selectedDepartments)
            ? selectedDepartments
              .filter((v) => v !== null && v !== undefined)
              .map((v) => String(v))
            : [];

          const hasAll = normalized.some((v) => v.toLowerCase() === "all");
          if (hasAll) return ["all"];
          if (normalized.length === 0) return ["all"];
          return normalized;
        })(),
        use_memory: Boolean(useMemory),
        messages: Array.isArray(messages) ? messages : [],
        language: normalizeDocsChatbotLanguage(language),
      }),
    };

    try {
      const response = await fetch(endpoint, requestInit);
      if (!response.body) throw new Error("ReadableStream not supported");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let botResponse = "";
      let gotContext = false;
      let pending = "";
      let indexedSources = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        pending += chunk;

        if (!gotContext) {
          const result = this.streamTextUtils.extractLeadingJSON(pending);
          if (result) {
            gotContext = true;
            indexedSources = this.streamTextUtils.indexSourcesByContextId(
              result.obj?.sources,
            );
            pending = result.rest;
          } else {
            continue;
          }
        }

        if (pending) {
          botResponse += pending;
          pending = "";
        }

        onDelta?.({ text: botResponse, contextArray: indexedSources });
      }

      onDone?.({ contextArray: indexedSources });
    } catch (e) {
      // Fallback: retry once without streaming
      try {
        const retry = await fetch(endpoint, requestInit);
        const fullText = await retry.text();
        let ctx = [];
        let textPart = fullText;
        const res = this.streamTextUtils.extractLeadingJSON(fullText);
        if (res) {
          ctx = this.streamTextUtils.indexSourcesByContextId(res.obj?.sources);
          textPart = res.rest;
        }
        onDelta?.({ text: textPart || "", contextArray: ctx });
        onDone?.({ contextArray: ctx });
      } catch (fallbackErr) {
        onError?.(fallbackErr);
        return;
      }
    }
  }
}
